import * as Phaser from 'phaser';
import {
  emitAttemptMetrics,
  emitGameOver,
  emitGameState,
  emitLevelClear,
  emitTimeTick,
  gameEventNames,
  onGameEvent,
} from '../events';
import { buildLevelByNumber } from '../levels';
import { Board } from '../model/Board';
import { directionVectors } from '../model/cells';
import type { Cell } from '../model/types';
import { LevelSession, pickHintCandidate } from '../session';
import { LevelTimer } from '../timer';
import {
  cellSize,
  checkedColor,
  exitDurationDivisor,
  exitDurationMax,
  exitDurationMin,
  exitEasePeakSlope,
  exitFlashColor,
  exitFlightColor,
  guideColor,
  hintColor,
  hoverColor,
  invalidColor,
  launchEase,
  lineColor,
  wakeDepth,
  wakeMaxSpan,
  wakeTrailDuration,
} from './arrowStyle';
import { ArrowRenderer, type ArrowView } from './arrowRenderer';
import { CameraControls } from './cameraControls';
import { nearestCandidate } from './hitTest';

const longPressDuration = 430;
/** タップとドラッグを分ける移動量（CSS ピクセル）。 */
const dragThreshold = 9;
const collisionClearance = 20;
const autoLaunchStaggerDelay = 140;
/** 盤面の外周に残す余白（ワールド単位）。 */
const boardMargin = cellSize * 0.55;
/** 画面端に残す余白（CSS ピクセル）。 */
const viewportMargin = 6;
/** ズームが落ち着いたと見なすまでの時間。この後に焼き込み解像度を合わせ直す。 */
const zoomSettleDelay = 200;

interface PointerRecord {
  readonly startX: number;
  readonly startY: number;
  lastX: number;
  lastY: number;
}

interface TapCandidate {
  readonly pointerId: number;
  readonly arrowId: string;
}

interface PressCandidate extends TapCandidate {
  readonly timer: Phaser.Time.TimerEvent;
  triggered: boolean;
}

interface GuideLine {
  readonly start: Cell;
  readonly end: Cell;
}

interface LaunchArrowOptions {
  readonly tapPoint?: Cell;
}

function distanceBetweenPointers(records: ReadonlyMap<number, PointerRecord>): number | null {
  const [first, second] = [...records.values()];
  if (first === undefined || second === undefined) {
    return null;
  }
  return Phaser.Math.Distance.Between(first.lastX, first.lastY, second.lastX, second.lastY);
}

function midpointBetweenPointers(records: ReadonlyMap<number, PointerRecord>): Cell | null {
  const [first, second] = [...records.values()];
  if (first === undefined || second === undefined) {
    return null;
  }
  return {
    x: (first.lastX + second.lastX) / 2,
    y: (first.lastY + second.lastY) / 2,
  };
}

export class PuzzleScene extends Phaser.Scene {
  private board!: Board;
  private session!: LevelSession;
  private currentLevelNumber!: number;
  private timer = new LevelTimer();
  private arrowRenderer!: ArrowRenderer;
  private readonly pointerRecords = new Map<number, PointerRecord>();
  private readonly guideLines: GuideLine[] = [];
  private readonly checkedArrowIds = new Set<string>();
  private readonly pendingAutoLaunchIds = new Set<string>();
  private tapCandidate: TapCandidate | null = null;
  private pressCandidate: PressCandidate | null = null;
  private lastPinchDistance: number | null = null;
  private cameraControls!: CameraControls;
  private nextAutoLaunchAt = 0;
  private prefersReducedMotion = false;
  private interactionLocked = false;
  private gameOverLocked = false;
  private hoveredArrowId: string | null = null;
  private observedZoom = 0;
  private zoomSettleAt = 0;
  private guideGraphics!: Phaser.GameObjects.Graphics;
  private readonly commandDisposers: Array<() => void> = [];
  constructor(private readonly initialLevelNumber: number) {
    super({ key: 'PuzzleScene' });
  }

  init(data: { levelNumber?: number } = {}): void {
    const levelNumber = data.levelNumber ?? this.initialLevelNumber;
    this.board = new Board(buildLevelByNumber(levelNumber));
    this.session = new LevelSession();
    this.currentLevelNumber = levelNumber;
    this.resetInteractionState();
  }

  private resetInteractionState(): void {
    this.arrowRenderer?.resetArrowViews();
    this.pointerRecords.clear();
    this.guideLines.length = 0;
    this.checkedArrowIds.clear();
    this.pendingAutoLaunchIds.clear();
    this.tapCandidate = null;
    this.pressCandidate = null;
    this.lastPinchDistance = null;
    this.cameraControls?.reset();
    this.nextAutoLaunchAt = 0;
    this.prefersReducedMotion = false;
    this.interactionLocked = false;
    this.gameOverLocked = false;
    this.hoveredArrowId = null;
    this.observedZoom = 0;
    this.zoomSettleAt = 0;
  }

  /** ゲーム単位（物理ピクセル）と CSS ピクセルの比。 = devicePixelRatio。 */
  private get renderScale(): number {
    return 1 / this.scale.zoom;
  }

  create(): void {
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.arrowRenderer = new ArrowRenderer(
      this,
      this.board,
      this.currentLevelNumber,
      (view) => this.resolveRestingColor(view),
      () => this.hoveredArrowId,
      () => this.prefersReducedMotion,
    );
    // 焼き込み層の解像度はカメラ倍率から決まるので、カメラを先に組む。
    this.cameraControls = new CameraControls(
      this.cameras.main,
      this.scale,
      this.board.level.width * cellSize,
      this.board.level.height * cellSize,
      boardMargin,
      viewportMargin * this.renderScale,
    );
    this.cameraControls.configure(true);
    this.arrowRenderer.createStaticBoardLayers(this.cameras.main.zoom);
    this.arrowRenderer.bakeGridLayer();
    this.guideGraphics = this.add.graphics().setDepth(5);
    this.arrowRenderer.createArrowViews();
    this.arrowRenderer.markStaticArrowLayerDirty();
    this.arrowRenderer.flushStaticArrowLayerBake();
    this.timer = new LevelTimer();
    this.observedZoom = this.cameras.main.zoom;
    this.zoomSettleAt = 0;
    this.configureInput();
    this.configureCommands();
    this.emitState('進路が空いている矢印をタップ', 'neutral');
    // ヘッダーの経過時間表示。モーダル中は Phaser ループごと sleep するので
    // tick も止まり、activeElapsedMs 側の除外と合わせて表示が進まない。
    emitTimeTick({ elapsedMs: 0 });
    this.time.addEvent({
      delay: 500,
      loop: true,
      callback: () => emitTimeTick({ elapsedMs: this.activeElapsedMs() }),
    });

    this.scale.on(Phaser.Scale.Events.RESIZE, this.handleResize, this);
    this.arrowRenderer.listenForWebGlRestore();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanUp, this);
  }

  /**
   * ピンチ・ホイールでカメラ倍率が変わったら、止まってから焼き込み層を
   * その倍率へ作り直す。拡大中に毎フレーム作り直すと大盤面でコマ落ちするため、
   * 動いている間は据え置く。
   */
  update(time: number): void {
    const zoom = this.cameras.main.zoom;
    if (Math.abs(zoom - this.observedZoom) > 0.0001) {
      this.observedZoom = zoom;
      this.zoomSettleAt = time + zoomSettleDelay;
      return;
    }
    if (this.zoomSettleAt === 0 || time < this.zoomSettleAt) {
      return;
    }
    this.zoomSettleAt = 0;
    this.arrowRenderer.syncLayerScale(zoom);
  }

  private resolveRestingColor(view: ArrowView): number {
    const arrowId = view.definition.id;
    if (this.session.isFailed(arrowId)) {
      return invalidColor;
    }
    if (this.session.isHinted(arrowId)) {
      return hintColor;
    }
    return this.checkedArrowIds.has(arrowId) ? checkedColor : lineColor;
  }

  private findNearestArrow(point: Cell): string | null {
    const candidates = this.board.activeArrows.flatMap((definition) => {
      const view = this.arrowRenderer.getView(definition.id);
      return view === undefined || view.busy
        ? []
        : [{ id: definition.id, points: view.hitRoute }];
    });
    return nearestCandidate(point, candidates, cellSize * 0.75);
  }

  private updateHover(pointer: Phaser.Input.Pointer): void {
    if (this.interactionLocked || this.gameOverLocked) {
      return;
    }

    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const arrowId = this.findNearestArrow(worldPoint);
    if (arrowId === this.hoveredArrowId) {
      this.input.setDefaultCursor(arrowId === null ? 'default' : 'pointer');
      return;
    }

    const previousView = this.hoveredArrowId === null
      ? undefined
      : this.arrowRenderer.getView(this.hoveredArrowId);
    if (previousView !== undefined && !previousView.busy) {
      this.arrowRenderer.markStaticArrowLayerDirty();
    }

    this.hoveredArrowId = arrowId;
    const view = arrowId === null ? undefined : this.arrowRenderer.getView(arrowId);
    if (view !== undefined && !view.busy) {
      const restingColor = this.resolveRestingColor(view);
      this.arrowRenderer.drawArrow(view, restingColor === lineColor ? hoverColor : restingColor);
      this.arrowRenderer.markStaticArrowLayerDirty();
    }
    this.arrowRenderer.flushStaticArrowLayerBake();
    this.input.setDefaultCursor(arrowId === null ? 'default' : 'pointer');
  }

  private configureInput(): void {
    this.input.on('pointerdown', this.handlePointerDown, this);
    this.input.on('pointermove', this.handlePointerMove, this);
    this.input.on('pointerup', this.handlePointerUp, this);
    this.input.on('pointerupoutside', this.handlePointerUp, this);
    this.input.on('wheel', this.handleWheel, this);
  }

  private configureCommands(): void {
    this.commandDisposers.push(
      onGameEvent(gameEventNames.restart, () => this.restartLevel()),
      onGameEvent(gameEventNames.setLevel, (detail) => {
        this.tweens.killAll();
        this.scene.restart({ levelNumber: detail.levelNumber });
      }),
      onGameEvent(gameEventNames.continue, (detail) => {
        if (detail.levelNumber !== this.currentLevelNumber) {
          return;
        }
        this.session.continueRun();
        this.emitAttemptMetrics();
        this.gameOverLocked = false;
        this.scheduleResolvedFailureLaunches([...this.pendingAutoLaunchIds]);
        this.emitState('進路が空いている矢印をタップ', 'neutral');
      }),
      onGameEvent(gameEventNames.hintGranted, (detail) => {
        if (detail.levelNumber === this.currentLevelNumber) {
          this.grantHint();
        }
      }),
      onGameEvent(gameEventNames.timePause, () => this.pauseTimer()),
      onGameEvent(gameEventNames.timeResume, () => this.resumeTimer()),
    );
  }

  private beginLongPress(pointerId: number, arrowId: string): void {
    if (this.interactionLocked || this.gameOverLocked) {
      return;
    }
    this.cancelLongPress();
    const timer = this.time.delayedCall(longPressDuration, () => {
      const candidate = this.pressCandidate;
      const view = this.arrowRenderer.getView(arrowId);
      if (
        candidate?.pointerId !== pointerId ||
        candidate.arrowId !== arrowId ||
        !this.pointerRecords.has(pointerId) ||
        view === undefined ||
        view.busy ||
        this.gameOverLocked
      ) {
        return;
      }
      candidate.triggered = true;
      this.tapCandidate = null;
      this.addGuideLine(view);
    });
    this.pressCandidate = { pointerId, arrowId, timer, triggered: false };
  }

  private cancelLongPress(pointerId?: number): void {
    if (
      this.pressCandidate === null ||
      (pointerId !== undefined && this.pressCandidate.pointerId !== pointerId)
    ) {
      return;
    }
    if (!this.pressCandidate.timer.hasDispatched) {
      this.pressCandidate.timer.remove(false);
    }
    this.pressCandidate = null;
  }

  private addGuideLine(view: ArrowView): void {
    const tipCell = view.definition.cells.at(-1);
    if (tipCell === undefined) {
      return;
    }
    const start = this.arrowRenderer.cellToWorld(tipCell);
    const direction = directionVectors[view.definition.direction];
    const distance = this.arrowRenderer.getDistanceFromTipToOutside(
      start,
      view.definition.direction,
    );
    this.guideLines.push({
      start,
      end: {
        x: start.x + direction.x * distance,
        y: start.y + direction.y * distance,
      },
    });
    this.checkedArrowIds.add(view.definition.id);
    this.arrowRenderer.drawArrow(view, this.resolveRestingColor(view));
    this.arrowRenderer.markStaticArrowLayerDirty();
    this.arrowRenderer.flushStaticArrowLayerBake();
    this.drawGuideLines();
    this.emitState('進路の先を盤面に残しました', 'neutral');
  }

  private drawGuideLines(): void {
    this.guideGraphics.clear();
    this.guideGraphics.lineStyle(2, guideColor, 0.62);
    this.guideLines.forEach((guide) => {
      this.guideGraphics.lineBetween(guide.start.x, guide.start.y, guide.end.x, guide.end.y);
      this.guideGraphics.strokeCircle(guide.start.x, guide.start.y, 7);
    });
  }

  private clearGuideLines(): void {
    this.guideLines.length = 0;
    this.guideGraphics.clear();
    this.checkedArrowIds.clear();
    this.arrowRenderer.markStaticArrowLayerDirty();
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    this.pointerRecords.set(pointer.id, {
      startX: pointer.position.x,
      startY: pointer.position.y,
      lastX: pointer.position.x,
      lastY: pointer.position.y,
    });

    if (this.pointerRecords.size === 1 && !this.gameOverLocked) {
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const arrowId = this.findNearestArrow(worldPoint);
      if (arrowId !== null) {
        this.tapCandidate = { pointerId: pointer.id, arrowId };
        this.beginLongPress(pointer.id, arrowId);
      }
    }

    if (this.pointerRecords.size > 1) {
      this.tapCandidate = null;
      this.cancelLongPress();
      this.lastPinchDistance = distanceBetweenPointers(this.pointerRecords);
    }
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!pointer.isDown) {
      this.updateHover(pointer);
      return;
    }

    const record = this.pointerRecords.get(pointer.id);
    if (record === undefined) {
      return;
    }

    const deltaX = pointer.position.x - record.lastX;
    const deltaY = pointer.position.y - record.lastY;
    record.lastX = pointer.position.x;
    record.lastY = pointer.position.y;

    if (this.pointerRecords.size > 1) {
      this.tapCandidate = null;
      this.applyPinchZoom();
      return;
    }

    const travel = Phaser.Math.Distance.Between(
      record.startX,
      record.startY,
      record.lastX,
      record.lastY,
    );
    if (travel < dragThreshold * this.renderScale) {
      return;
    }

    this.tapCandidate = null;
    this.cancelLongPress(pointer.id);
    const camera = this.cameras.main;
    camera.scrollX = camera.clampX(camera.scrollX - deltaX / camera.zoom);
    camera.scrollY = camera.clampY(camera.scrollY - deltaY / camera.zoom);
  }

  private applyPinchZoom(): void {
    const distance = distanceBetweenPointers(this.pointerRecords);
    const midpoint = midpointBetweenPointers(this.pointerRecords);
    if (distance === null || midpoint === null || this.lastPinchDistance === null) {
      this.lastPinchDistance = distance;
      return;
    }

    const factor = distance / this.lastPinchDistance;
    this.cameraControls.applyPinch(factor, midpoint);
    this.lastPinchDistance = distance;
  }

  private handleWheel(
    pointer: Phaser.Input.Pointer,
    _currentlyOver: Phaser.GameObjects.GameObject[],
    _deltaX: number,
    deltaY: number,
  ): void {
    this.cameraControls.applyWheel(pointer.position.x, pointer.position.y, deltaY);
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer): void {
    const record = this.pointerRecords.get(pointer.id);
    const isSinglePointer = this.pointerRecords.size === 1;
    const candidate = this.tapCandidate;
    const wasLongPress =
      this.pressCandidate?.pointerId === pointer.id && this.pressCandidate.triggered;

    this.pointerRecords.delete(pointer.id);
    this.lastPinchDistance = distanceBetweenPointers(this.pointerRecords);
    this.cancelLongPress(pointer.id);

    if (
      record !== undefined &&
      isSinglePointer &&
      !wasLongPress &&
      candidate?.pointerId === pointer.id &&
      Phaser.Math.Distance.Between(record.startX, record.startY, pointer.position.x, pointer.position.y) <
        dragThreshold * this.renderScale
    ) {
      this.selectArrow(
        candidate.arrowId,
        this.cameras.main.getWorldPoint(pointer.position.x, pointer.position.y),
      );
    }

    if (this.pointerRecords.size === 0) {
      this.tapCandidate = null;
    }
  }

  private selectArrow(arrowId: string, tapPoint: Cell): void {
    const view = this.arrowRenderer.getView(arrowId);
    if (view === undefined || view.busy || this.interactionLocked || this.gameOverLocked) {
      return;
    }

    const result = this.board.tryRemove(arrowId);
    if (result.kind === 'missing') {
      return;
    }

    this.clearGuideLines();
    if (result.kind === 'blocked') {
      this.showBlockedFeedback(view, result.distanceToBlocker ?? 1);
      this.arrowRenderer.flushStaticArrowLayerBake();
      return;
    }

    this.launchArrow(view, { tapPoint });
  }

  private launchArrow(view: ArrowView, options: LaunchArrowOptions = {}): void {
    const arrowId = view.definition.id;
    this.pendingAutoLaunchIds.delete(arrowId);
    this.session.recordRemoved(arrowId);
    const resolvedFailures = this.session.clearResolvedFailures(
      (failedArrowId) => this.board.canRemove(failedArrowId),
    );
    for (const resolvedArrowId of resolvedFailures) {
      this.pendingAutoLaunchIds.add(resolvedArrowId);
    }
    this.scheduleResolvedFailureLaunches(resolvedFailures);
    this.arrowRenderer.markStaticArrowLayerDirty();

    if (this.hoveredArrowId === arrowId) {
      this.hoveredArrowId = null;
    }
    view.busy = true;
    // 0 フレーム目。tween の最初の onUpdate は次フレームなので、閃光はここで焼く。
    const flashStartedAt = this.time.now;
    this.arrowRenderer.drawArrow(
      view,
      this.prefersReducedMotion ? exitFlightColor : exitFlashColor,
    );
    this.arrowRenderer.markStaticArrowLayerDirty();
    this.arrowRenderer.flushStaticArrowLayerBake();
    if (options.tapPoint !== undefined) {
      this.arrowRenderer.spawnTapPing(options.tapPoint);
    }

    if (this.board.isComplete) {
      // クリア確定の瞬間で計時を固定する。結果画面の裏でヘッダーの TIME が
      // 進み続けないよう、以降の tick も凍結値を報告する。
      this.timer.freeze();
      emitLevelClear({
        levelNumber: this.currentLevelNumber,
        arrowTotal: this.board.totalCount,
        elapsedMs: this.activeElapsedMs(),
        reachedGameOver: this.session.hasReachedGameOver,
      });
    }

    const duration = this.prefersReducedMotion
      ? 1
      : Phaser.Math.Clamp(
        view.successProgress / exitDurationDivisor,
        exitDurationMin,
        exitDurationMax,
      );
    // 航跡は「直近 wakeTrailDuration ms に進んだ距離」なので、フレームレートに依存しない。
    const peakSpeed = view.successProgress * exitEasePeakSlope / duration;
    const wakeSpan = Math.min(peakSpeed * wakeTrailDuration, wakeMaxSpan);
    const wake = this.prefersReducedMotion
      ? null
      : this.add.graphics().setDepth(wakeDepth);

    this.tweens.add({
      targets: view,
      progress: view.successProgress,
      duration,
      ease: this.prefersReducedMotion ? 'Linear' : launchEase,
      onUpdate: wake === null
        // reduced-motion。動きはほぼ見えないが、識別色だけは残す。
        ? () => this.arrowRenderer.drawArrow(view, exitFlightColor)
        : () => this.arrowRenderer.drawExitFrame(view, wake, wakeSpan, flashStartedAt),
      onComplete: () => {
        view.container.destroy(true);
        this.arrowRenderer.deleteView(arrowId);
        this.arrowRenderer.fadeOutWake(wake);
        if (this.board.isComplete && this.arrowRenderer.viewCount === 0) {
          this.emitState('クリア！', 'success', true);
        }
      },
    });

    if (this.board.isComplete) {
      this.emitState('最後の矢印が飛び立ちます', 'success');
    } else if (options.tapPoint === undefined) {
      // タップ由来でない＝自動発射。飛んだ理由が分かるよう一言だけ添える
      // （ピングは「タップを受け付けた」印なので、ここでは出さない）。
      this.emitState('ふさがっていた矢印も飛び立ちました', 'success');
    } else {
      this.emitState('進路が空いている矢印をタップ', 'neutral');
    }
  }

  private scheduleResolvedFailureLaunches(arrowIds: readonly string[]): void {
    const stagger = this.prefersReducedMotion ? 1 : autoLaunchStaggerDelay;
    for (const arrowId of arrowIds) {
      this.nextAutoLaunchAt = Math.max(this.time.now, this.nextAutoLaunchAt) + stagger;
      this.time.delayedCall(
        Math.max(1, this.nextAutoLaunchAt - this.time.now),
        () => this.launchResolvedFailure(arrowId),
      );
    }
  }

  private launchResolvedFailure(arrowId: string): void {
    if (!this.pendingAutoLaunchIds.has(arrowId) || !this.sys.isActive()) {
      return;
    }
    const view = this.arrowRenderer.getView(arrowId);
    if (view === undefined || !view.container.active || this.board.getArrow(arrowId) === undefined) {
      this.pendingAutoLaunchIds.delete(arrowId);
      return;
    }
    if (this.gameOverLocked) {
      return;
    }
    if (view.busy) {
      // 再試行も共通のスロット割り当てへ戻し、他の予約と同フレーム発射に
      // ならないようにする（スタガー保証の維持）。
      this.scheduleResolvedFailureLaunches([arrowId]);
      return;
    }

    const result = this.board.tryRemove(arrowId);
    if (result.kind !== 'removed') {
      this.scheduleResolvedFailureLaunches([arrowId]);
      return;
    }
    this.pendingAutoLaunchIds.delete(arrowId);
    this.launchArrow(view);
  }

  private showBlockedFeedback(view: ArrowView, distanceToBlocker: number): void {
    if (view.busy) {
      return;
    }
    this.interactionLocked = true;
    if (this.hoveredArrowId === view.definition.id) {
      this.hoveredArrowId = null;
    }
    view.busy = true;
    this.arrowRenderer.drawArrow(view, lineColor);
    this.arrowRenderer.markStaticArrowLayerDirty();
    this.emitState('進路が他の矢印でふさがっています', 'warning');

    const collisionProgress = Math.max(6, distanceToBlocker * cellSize - collisionClearance);

    this.tweens.add({
      targets: view,
      progress: collisionProgress,
      duration: this.prefersReducedMotion
        ? 1
        : Phaser.Math.Clamp(collisionProgress / 1.6, 65, 180),
      ease: 'Quad.easeIn',
      onUpdate: () => this.arrowRenderer.drawArrow(view, view.color),
      onComplete: () => {
        if (!view.container.active) {
          return;
        }
        this.arrowRenderer.drawArrow(view, invalidColor);
        this.cameras.main.shake(
          this.prefersReducedMotion ? 1 : 115,
          this.prefersReducedMotion ? 0 : 0.0085,
          true,
        );
        this.emitState('ぶつかって元の位置に戻ります', 'warning');
        this.time.delayedCall(this.prefersReducedMotion ? 1 : 30, () => {
          if (this.arrowRenderer.getView(view.definition.id) !== view || !view.container.active) {
            return;
          }
          this.tweens.add({
            targets: view,
            progress: 0,
            duration: this.prefersReducedMotion
              ? 1
              : Phaser.Math.Clamp(collisionProgress / 1.9, 60, 140),
            ease: 'Quad.easeOut',
            onUpdate: () => this.arrowRenderer.drawArrow(view, view.color),
            onComplete: () => {
              view.progress = 0;
              view.busy = false;
              const reachedGameOver = this.session.recordBlocked(view.definition.id);
              this.emitAttemptMetrics();
              this.interactionLocked = false;
              if (reachedGameOver) {
                this.gameOverLocked = true;
                this.input.setDefaultCursor('default');
              }
              this.arrowRenderer.drawArrow(view, this.resolveRestingColor(view));
              this.arrowRenderer.markStaticArrowLayerDirty();
              this.arrowRenderer.flushStaticArrowLayerBake();
              this.emitState('ぶつかって元の位置に戻ります', 'warning');
              if (reachedGameOver) {
                emitGameOver({
                  levelNumber: this.currentLevelNumber,
                  remaining: this.board.remainingCount,
                  total: this.board.totalCount,
                  failCount: this.session.failCount,
                  elapsedMs: this.activeElapsedMs(),
                });
              }
            },
          });
        });
      },
    });
  }

  private grantHint(): void {
    const arrowId = this.hintCandidate();
    if (arrowId === null) {
      this.emitState('動かせる矢印がありません', 'warning');
      return;
    }

    const view = this.arrowRenderer.getView(arrowId);
    if (view === undefined || view.busy) {
      return;
    }

    if (this.hoveredArrowId === arrowId) {
      this.hoveredArrowId = null;
    }
    this.session.markHinted(arrowId);
    this.emitAttemptMetrics();
    this.arrowRenderer.drawArrow(view, this.resolveRestingColor(view));
    this.arrowRenderer.markStaticArrowLayerDirty();
    this.arrowRenderer.flushStaticArrowLayerBake();
    this.emitState('光っている矢印は外に出せます', 'neutral', false, this.hasHintCandidate());
  }

  private hintCandidate(): string | null {
    return pickHintCandidate(
      this.board.removableArrowIds,
      (arrowId) => this.session.isHinted(arrowId),
      (arrowId) => this.arrowRenderer.getView(arrowId)?.busy ?? true,
    );
  }

  private emitAttemptMetrics(): void {
    emitAttemptMetrics({
      levelNumber: this.currentLevelNumber,
      failCount: this.session.attemptFailCount,
      reachedGameOver: this.session.hasReachedGameOver,
      continuedCount: this.session.continuedCount,
      hintUsedCount: this.session.hintUsedCount,
    });
  }

  private hasHintCandidate(): boolean {
    return this.board.activeArrows.some((arrow) => {
      const view = this.arrowRenderer.getView(arrow.id);
      return !this.session.isHinted(arrow.id) && view !== undefined && !view.busy
        && this.board.canRemove(arrow.id);
    });
  }

  private pauseTimer(): void {
    this.timer.pause();
  }

  private resumeTimer(): void {
    this.timer.resume();
  }

  private activeElapsedMs(): number {
    return this.timer.elapsedMs();
  }

  private restartLevel(): void {
    this.tweens.killAll();
    this.scene.restart({ levelNumber: this.currentLevelNumber });
  }

  private handleResize(): void {
    this.cameraControls.configure(false);
    // 画面サイズが変わると盤面を収める倍率も変わる。次の update で焼き直す。
    this.observedZoom = this.cameras.main.zoom;
    this.zoomSettleAt = this.time.now + zoomSettleDelay;
  }

  private emitState(
    message: string,
    tone: 'neutral' | 'success' | 'warning',
    complete = false,
    hintAvailable = this.hasHintCandidate(),
  ): void {
    emitGameState({
      remaining: this.board.remainingCount,
      total: this.board.totalCount,
      failCount: this.session.failCount,
      maxFailures: this.session.maxFailures,
      hintAvailable,
      message,
      tone,
      complete,
    });
  }

  private cleanUp(): void {
    this.cancelLongPress();
    this.hoveredArrowId = null;
    this.arrowRenderer.destroy();
    this.input.setDefaultCursor('default');
    this.scale.off(Phaser.Scale.Events.RESIZE, this.handleResize, this);
    this.input.off('pointerdown', this.handlePointerDown, this);
    this.input.off('pointermove', this.handlePointerMove, this);
    this.input.off('pointerup', this.handlePointerUp, this);
    this.input.off('pointerupoutside', this.handlePointerUp, this);
    this.input.off('wheel', this.handleWheel, this);
    this.commandDisposers.forEach((dispose) => dispose());
    this.commandDisposers.length = 0;
  }
}
